/**
 * Knowledge API Client
 * 
 * Typed functions for all knowledge-related endpoints.
 * Implements endpoints from API_CONTRACTS.md.
 */

import { apiClient } from './client';
import type {
  KnowledgeItem,
  KnowledgeListResponse,
  ItemStatusResponse,
  SearchResponse,
  DeleteResponse,
  IngestURLResponse,
} from '../types/api';

/**
 * Query parameters for getAll()
 */
export interface GetKnowledgeParams {
  source_type?: 'youtube' | 'instagram' | 'reel' | 'note' | 'voice' | 'text' | 'file' | 'upload' | 'paste' | 'pdf' | 'markdown' | 'textfile' | 'image';
  category_label?: string;
  tag?: string;
  created_after?: string;
  limit?: number;
  offset?: number;
  include_processing?: boolean;
  include_failed?: boolean;
}

/**
 * Query parameters for search()
 */
export interface SearchParams {
  q: string;
  limit?: number;
  source_type?: 'reel' | 'youtube' | 'note' | 'voice' | 'text' | 'file' | 'upload' | 'paste' | 'pdf' | 'markdown' | 'textfile' | 'image';
}

/**
 * Get all knowledge items (paginated)
 * GET /knowledge
 */
export async function getAll(params?: GetKnowledgeParams): Promise<KnowledgeListResponse> {
  const queryParams = new URLSearchParams();
  
  if (params?.source_type) queryParams.append('source_type', params.source_type);
  if (params?.category_label) queryParams.append('category_label', params.category_label);
  if (params?.tag) queryParams.append('tag', params.tag);
  if (params?.created_after) queryParams.append('created_after', params.created_after);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.include_processing) queryParams.append('include_processing', 'true');
  if (params?.include_failed) queryParams.append('include_failed', 'true');
  
  const query = queryParams.toString();
  const endpoint = query ? `/knowledge?${query}` : '/knowledge';
  
  return apiClient.get<KnowledgeListResponse>(endpoint);
}

/**
 * Get available filter options from database
 * GET /knowledge/filters/options
 */
export async function getFilterOptions(): Promise<{
  source_types: Array<{ value: string; count: number }>;
  categories: Array<{ value: string; count: number }>;
  tags: Array<{ value: string; count: number }>;
}> {
  return apiClient.get('/knowledge/filters/options');
}

/**
 * Get a single knowledge item by ID
 * GET /knowledge/{item_id}
 */
export async function getById(itemId: string): Promise<KnowledgeItem> {
  return apiClient.get<KnowledgeItem>(`/knowledge/${itemId}`);
}

/**
 * Get knowledge item status (for polling during ingestion)
 * GET /knowledge/{item_id}/status
 */
export async function getStatus(itemId: string): Promise<ItemStatusResponse> {
  return apiClient.get<ItemStatusResponse>(`/knowledge/${itemId}/status`);
}

/**
 * Full transcript / video-analysis content for a knowledge item
 * GET /knowledge/{item_id}/full-content
 */
export interface FullContentResponse {
  full_text: string;
  chunk_count: number;
  content_type: string;
  sections: Array<{ heading: string; level: number; content: string }>;
}

export async function getFullContent(itemId: string): Promise<FullContentResponse> {
  return apiClient.get<FullContentResponse>(`/knowledge/${itemId}/full-content`);
}

export interface MediaURLResponse {
  url: string;
  filename: string;
  content_type: string;
  media_kind: string;
  size_bytes?: number | null;
  expires_in: number;
}

export async function getMediaURL(itemId: string): Promise<MediaURLResponse> {
  return apiClient.get<MediaURLResponse>(`/knowledge/${itemId}/media-url`);
}

/**
 * Delete a knowledge item
 * DELETE /knowledge/{item_id}
 */
export async function deleteItem(itemId: string): Promise<DeleteResponse> {
  return apiClient.delete<DeleteResponse>(`/knowledge/${itemId}`);
}

/**
 * Retry a failed knowledge item
 * POST /knowledge/{item_id}/retry
 */
export async function retryItem(itemId: string): Promise<IngestURLResponse> {
  return apiClient.post<IngestURLResponse>(`/knowledge/${itemId}/retry`, {});
}

/**
 * Resume a cancelled or interrupted URL-based knowledge item
 * POST /knowledge/{item_id}/resume
 */
export async function resumeItem(itemId: string): Promise<IngestURLResponse> {
  return apiClient.post<IngestURLResponse>(`/knowledge/${itemId}/resume`, {});
}

/**
 * Cancel a currently processing knowledge item
 * POST /knowledge/{item_id}/cancel
 */
export async function cancelItem(itemId: string): Promise<IngestURLResponse> {
  return apiClient.post<IngestURLResponse>(`/knowledge/${itemId}/cancel`, {});
}

export async function reviewCategory(
  itemId: string,
  payload: {
    primary_category: string;
    subcategory?: string;
    tags?: string[];
    reasoning?: string;
    category_reason?: string;
    subcategory_reason?: string;
    note?: string;
  }
): Promise<unknown> {
  return apiClient.patch(`/entities/knowledge/${itemId}/category`, payload);
}

/**
 * Semantic search across knowledge base
 * GET /search
 */
export async function search(params: SearchParams): Promise<SearchResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append('q', params.q);
  
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.source_type) queryParams.append('source_type', params.source_type);
  
  return apiClient.get<SearchResponse>(`/search?${queryParams.toString()}`);
}
